import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import * as momentImported from 'moment';

import { SetLanguage, SetLanguages } from '../store/actions';
import { ConfigService } from './config.service';
import { ICmxClientConfig } from '../typings/client-config';
import { Language } from '../typings/language';
import { State } from '../typings/state';
import { getLocalStorageItem, setLocalStorageItem } from '../../vanilla/utils/safe-storage';

const moment = momentImported;

export const LS_LANG_KEY = 'language';

@Injectable({
  providedIn: 'root',
})
export class TranslationService {
  private counter = 0;
  private TIMES_TO_TRY_LOAD = 10;
  private config: ICmxClientConfig;

  private translateHost: string;
  private languagesUrl: string;

  private apiLanguageLoaded = false;

  constructor(
    private http: HttpClient,
    private translate: TranslateService,
    private store: Store<State>,
    private configService: ConfigService,
  ) {
    this.config = this.configService.getConfig();
    this.translateHost = this.config.translate.translateHost;
    this.languagesUrl = this.config.translate.getLanguagesUrl;
  }

  async preInit() {
    try {
      const english = await this.http.get('./assets/translate/en_US.json').toPromise();
      this.translate.setTranslation('en', english);
      !this.apiLanguageLoaded && this.translate.use('en');
      moment.locale('en');
    } catch (error) {
      console.error('Failed to load fallback translations');
    }
  }

  async init() {
    this.getLanguages();
    const lang = getLocalStorageItem(LS_LANG_KEY) ? getLocalStorageItem(LS_LANG_KEY) : 'en_US';
    this.setLanguage(lang);
  }

  async setLanguage(lang: string) {
    await this.loadLanguage(lang);
    setLocalStorageItem(LS_LANG_KEY, lang);
    sessionStorage.setItem(LS_LANG_KEY, lang);
    this.store.dispatch(new SetLanguage(lang));
    moment.locale(lang.substring(0, 2));
    if (!this.apiLanguageLoaded) {
      this.apiLanguageLoaded = true;
    }
  }

  private async loadLanguage(lang: string) {
    try {
      this.counter += 1;
      await this.translate.getTranslation(lang).toPromise();
      await this.translate.use(lang).toPromise();
    } catch (error) {
      console.error(`Failed to load ${lang}. Application will try load language again`);
      if (this.counter < this.TIMES_TO_TRY_LOAD) {
        setTimeout(() => this.loadLanguage(lang), 5000);
      } else {
        console.error(`Failed to load ${lang}.`);
      }
    }
  }

  async getLanguages() {
    try {
      const languages = await this.fetchLanguages();
      this.store.dispatch(new SetLanguages(languages));
    } catch (error) {
      console.error(`Failed to load languages.`);
    }
  }

  fetchLanguages() {
    return this.http.get<Language[]>(`${this.translateHost}${this.languagesUrl}`).toPromise();
  }
}
